// A point's own source documents, kept current while a view shows them. The
// backend extracts a document's text in the background and announces each
// status change on the map's editor room, so a `source document status` event
// for this point re-lists it; a request that a newer one overtook is dropped.
// The content form and the Quick Edit Bar can show the same point at once, so a
// change made in one re-lists every other view in this tab.

import { onScopeDispose, ref, watch } from 'vue';
import {
  attachSourceDocumentFile,
  attachSourceDocumentUrl,
  deleteSourceDocument,
  listSourceDocuments,
} from '../api/sources.api';
import type { SourceDocument, SourceDocumentStatusEvent } from '../types/SourceDocumentTypes';

export const SOURCE_DOCUMENT_STATUS_EVENT = 'source document status';

/** The part of the collaboration socket this composable uses. */
export interface StatusSocket {
  on(event: string, handler: (payload: SourceDocumentStatusEvent) => void): unknown;
  off(event: string, handler: (payload: SourceDocumentStatusEvent) => void): unknown;
}

export interface PointSourceDocumentsOptions {
  mapId: () => string | number | null;
  nodeId: () => number | null;
  /** A live getter: the socket is created after the Map Builder has loaded, and
   *  replaced on reconnect, so a snapshot would go stale. */
  getSocket: () => StatusSocket | null;
}

/** Bumped by every add or delete in this tab; each view re-lists on a bump it
 *  did not make itself. */
const localChanges = ref(0);

interface Problem {
  response?: { status?: number; data?: { detail?: string } };
}

// Errors are RFC 7807 problem bodies: the user-facing reason is `detail`. An
// empty string leaves the wording to the view.
function problemDetail(error: unknown): string {
  return (error as Problem).response?.data?.detail ?? '';
}

export function usePointSourceDocuments(options: PointSourceDocumentsOptions) {
  const documents = ref<SourceDocument[]>([]);
  const uploading = ref(false);
  /** Set when a call failed: the backend's reason, or '' for none given. */
  const error = ref<string | null>(null);

  let latest = 0;
  let ownChange = localChanges.value;

  async function refresh(): Promise<void> {
    const mapId = options.mapId();
    const nodeId = options.nodeId();
    const request = ++latest;
    if (mapId == null || nodeId == null) {
      documents.value = [];
      return;
    }
    const { data } = await listSourceDocuments(mapId, nodeId);
    if (request !== latest) return;
    documents.value = data.results ?? [];
  }

  // Run a change, then re-list; without a change, only re-list.
  async function run(action?: () => Promise<unknown>): Promise<void> {
    error.value = null;
    try {
      if (action) {
        await action();
        ownChange = ++localChanges.value;
      }
      await refresh();
    } catch (failure) {
      error.value = problemDetail(failure);
    }
  }

  function attach(send: (mapId: string | number, nodeId: number) => Promise<unknown>) {
    const mapId = options.mapId();
    const nodeId = options.nodeId();
    if (mapId == null || nodeId == null) return Promise.resolve();
    uploading.value = true;
    return run(() => send(mapId, nodeId)).finally(() => {
      uploading.value = false;
    });
  }

  /** Attach a file to the point. */
  function upload(file: File): Promise<void> {
    return attach((mapId, nodeId) => attachSourceDocumentFile(mapId, file, nodeId));
  }

  /** Attach a link to the point; the backend reads the page behind it. */
  function attachUrl(url: string): Promise<void> {
    return attach((mapId, nodeId) => attachSourceDocumentUrl(mapId, url, nodeId));
  }

  /** Delete a document and its text. One another editor deleted first is gone
   *  all the same. */
  function remove(documentId: number): Promise<void> {
    const mapId = options.mapId();
    if (mapId == null) return Promise.resolve();
    return run(() =>
      deleteSourceDocument(mapId, documentId).catch((failure: unknown) => {
        if ((failure as Problem).response?.status !== 404) throw failure;
      })
    );
  }

  function onStatus(event: SourceDocumentStatusEvent): void {
    if (event.node_id !== options.nodeId()) return;
    // Non-fatal: the list keeps its last known state until the next event.
    refresh().catch(() => undefined);
  }

  let bound: StatusSocket | null = null;
  function bind(socket: StatusSocket | null): void {
    if (socket === bound) return;
    bound?.off(SOURCE_DOCUMENT_STATUS_EVENT, onStatus);
    bound = socket;
    bound?.on(SOURCE_DOCUMENT_STATUS_EVENT, onStatus);
  }

  watch(options.getSocket, bind, { immediate: true });
  watch(
    () => [options.mapId(), options.nodeId()],
    () => run(),
    { immediate: true }
  );
  watch(localChanges, change => {
    if (change === ownChange) return;
    ownChange = change;
    void run();
  });
  onScopeDispose(() => bind(null));

  return {
    documents,
    uploading,
    error,
    refresh,
    upload,
    attachUrl,
    remove,
  };
}
